import { create } from "zustand";
import { persist, createJSONStorage } from "zustand/middleware";
import { apiRequest } from "services/api";

export interface CollectorStateModel {
  loading: boolean;
  products: any[];
  images: string[];
  loadedImages: any[];
  collectorData: any;
  error: any | null;
}

interface CollectorStore extends CollectorStateModel {
  addImage: (id: string) => void;
  removeImage: (uuid: string) => Promise<void>;
  loadImages: () => Promise<void>;
  clear: () => Promise<void>;
  generateLink: (expiryDate: string, images: any[]) => Promise<void>;
  addProduct: (id: string) => void;
}

export const useCollectorStore = create<CollectorStore>()(
  persist(
    (set, get) => ({
      loading: false,
      products: [],
      images: [],
      loadedImages: [],
      collectorData: null,
      error: null,

      addImage: (id) => {
        set({ collectorData: null });
        const { images } = get();
        if (!images.includes(id)) {
          set({ images: [...images, id] });
        }
      },

      removeImage: async (uuid) => {
        set({ collectorData: null });
        set((state) => ({
          images: state.images.filter((img) => img !== uuid),
        }));
        await get().loadImages();
      },

      loadImages: async () => {
        try {
          const data = await apiRequest({
            endpoint: "/products/images/search",
            method: "post",
            payload: { uuids: get().images },
          });
          set({ loadedImages: data });
        } catch {
          // error silently
        }
      },

      clear: async () => {
        set({
          products: [],
          images: [],
          loadedImages: [],
          collectorData: null,
          loading: false,
          error: null,
        });
        await get().loadImages();
      },

      generateLink: async (expiryDate, images) => {
        try {
          const data = await apiRequest({
            endpoint: "/collector/",
            method: "post",
            payload: { expiryDate, images, type: "link" },
          });
          set({ collectorData: data });
        } catch {
          // error silently
        }
      },

      addProduct: (id) => {
        const { products } = get();
        if (!products.includes(id)) {
          set({ products: [...products, id] });
        }
      },
    }),
    {
      name: "collector-store",
      storage: createJSONStorage(() => sessionStorage),
      partialize: (state) => ({
        images: state.images,
        products: state.products,
        collectorData: state.collectorData,
      }),
    }
  )
);
